import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    InputAdornment,
    Typography,
    Divider,
    CircularProgress,
    alpha
} from "@mui/material";
import { 
    MapPin, 
    Search, 
    X, 
    // Navigation 
} 
from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { ApiConfig } from "@shared/Config/config";
import { toast } from "sonner";

// Simple debounce hook
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

const LocationPickerModal = ({ open, onClose, onSelectLocation }) => {
    
    const theme = useTheme();
    const [searchValue, setSearchValue] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [autocompleteService, setAutocompleteService] = useState(null);
    const [placesService, setPlacesService] = useState(null);
    const debouncedSearch = useDebounce(searchValue, 500);

    // Initialize Google Maps services
    useEffect(() => {
        if (!window.google) {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${ApiConfig.googleMapKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                setAutocompleteService(new window.google.maps.places.AutocompleteService());
                setPlacesService(new window.google.maps.places.PlacesService(document.createElement("div")));
            };
            document.head.appendChild(script);
        } else if (!autocompleteService) {
            setAutocompleteService(new window.google.maps.places.AutocompleteService());
            setPlacesService(new window.google.maps.places.PlacesService(document.createElement("div")));
        }
    }, [autocompleteService]);

    useEffect(() => {
        if (debouncedSearch && autocompleteService) {
            setLoading(true);
            autocompleteService.getPlacePredictions(
                { input: debouncedSearch, options: { language: "en" } },

                (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        setSuggestions(predictions);
                    } else {
                        setSuggestions([]);
                    }
                    setLoading(false);
                }
            );
        } else {
            setSuggestions([]);
        }
    }, [debouncedSearch, autocompleteService]);

    const handleSelectPlace = (placeId, description) => {
        if (!placesService) return;

        setLoading(true);
        placesService.getDetails({ placeId }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                onSelectLocation(lat, lng, description);
                onClose();
            } else {
                toast.error("Could not fetch location details");
            }
            setLoading(false);
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"

        >
            <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight="700">Change Location</Typography>
                <IconButton onClick={onClose} size="small">
                    <X size={20} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, pb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Search for area, companies, petrol bunks..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={18} color={theme.palette.primary.main} />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue && (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setSearchValue("")} size="small">
                                        <X size={16} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderRadius: 3,
                                "& fieldset": { border: "none" },
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                            }
                        }}
                    />
                </Box>

                <Divider />

                <List sx={{ pt: 1, maxHeight: 400, overflow: "auto" }}>

                    {loading && suggestions.length === 0 && (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {suggestions.map((item) => (
                        <ListItem
                            key={item.place_id}
                            button
                            onClick={() => handleSelectPlace(item.place_id, item.description)}
                            sx={{
                                py: 1.5,
                                px: 3,
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <MapPin size={20} color={theme.palette.text.secondary} />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.structured_formatting.main_text}
                                secondary={item.structured_formatting.secondary_text}
                                primaryTypographyProps={{ fontWeight: 500 }}
                            />
                        </ListItem>
                    ))}

                    {!loading && suggestions.length === 0 && searchValue.length > 2 && (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">No locations found for "{searchValue}"</Typography>
                        </Box>
                    )}
                </List>
            </DialogContent>
        </Dialog>
    );
};

export default LocationPickerModal;
